import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useHistory } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import { makeStyles } from '@material-ui/core/styles'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Link from '@material-ui/core/Link'
import { PhoneField, EmailFieldWithSuggestions } from 'components/form-util'
import ProgressIndicator from 'components/progress-indicators/progress-indicator'
import StepDescriptionOfferings from '../components/step-description-offerings'
import { amplitudeEvent } from 'lib/amplitude'
import { redirectToLogin, redirectToSignup } from 'lib/auth'
import {
  digitsOnly,
  loadGuestBooking,
  saveGuestBooking,
  withGuestBookingParam,
} from '../guest-booking'

const useStyles = makeStyles(theme => ({
  formFields: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: 410,
    },
  },
  phoneField: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}))

const schema = yup.object().shape({
  studentFirstName: yup
    .string()
    .trim()
    .required('First name is required'),
  studentLastName: yup
    .string()
    .trim()
    .required('Last name is required'),
  attendeeType: yup
    .string()
    .oneOf(['adult', 'junior'])
    .required(),
  guardianFirstName: yup.string().when('attendeeType', {
    is: 'junior',
    then: yup
      .string()
      .trim()
      .required('First name is required'),
  }),
  guardianLastName: yup.string().when('attendeeType', {
    is: 'junior',
    then: yup
      .string()
      .trim()
      .required('Last name is required'),
  }),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  phone: yup
    .string()
    .required('Phone number is required')
    .test(
      'ten-digits',
      'Enter a 10-digit phone number',
      value => digitsOnly(value).length === 10,
    ),
})

const storedDefaults = () => {
  const stored = loadGuestBooking()
  if (!stored) {
    return { attendeeType: 'adult' }
  }
  return {
    studentFirstName: stored.student.firstName,
    studentLastName: stored.student.lastName,
    attendeeType: stored.student.isMinor ? 'junior' : 'adult',
    guardianFirstName: stored.student.isMinor ? stored.contact.firstName : '',
    guardianLastName: stored.student.isMinor ? stored.contact.lastName : '',
    email: stored.contact.email,
    phone: stored.contact.phone,
  }
}

const GuestStudentForm = ({
  currentUrl,
  showDescription = true,
  stepText = 'Step 2 of 3',
  progressValue = 50,
}) => {
  const classes = useStyles()
  const history = useHistory()
  const { register, handleSubmit, errors, control, watch } = useForm({
    defaultValues: storedDefaults(),
    resolver: yupResolver(schema),
  })
  const attendeeType = watch('attendeeType')

  useEffect(() => {
    amplitudeEvent('view-guest-details-form', { is_guest: true })
  }, [])
  const isJunior = attendeeType === 'junior'

  const onSubmit = values => {
    const student = {
      firstName: values.studentFirstName.trim(),
      lastName: values.studentLastName.trim(),
      isMinor: isJunior,
    }
    const contact = {
      firstName: isJunior ? values.guardianFirstName.trim() : student.firstName,
      lastName: isJunior ? values.guardianLastName.trim() : student.lastName,
      email: values.email.trim(),
      phone: digitsOnly(values.phone),
    }
    amplitudeEvent('submit-guest-details', {
      is_guest: true,
      attendee_type: values.attendeeType,
    })
    saveGuestBooking({ contact, student })
    history.push(withGuestBookingParam(currentUrl))
  }

  return (
    <Grid container direction="column">
      {showDescription && (
        <>
          <ProgressIndicator value={progressValue} />
          <Divider style={{ margin: '24px 0 32px' }} />
          <StepDescriptionOfferings
            stepText={stepText}
            title="Select a Student"
            description="Who will be attending the session?"
          />
        </>
      )}
      <Box color="text.secondary" mb={2}>
        <Typography variant="body2">
          <Link
            component="button"
            type="button"
            onClick={() => redirectToLogin('consumer')}
          >
            Log in
          </Link>
          {' or '}
          <Link
            component="button"
            type="button"
            onClick={() => redirectToSignup('consumer')}
          >
            Create an Account
          </Link>
          {' for a faster booking experience.'}
        </Typography>
      </Box>
      <form onSubmit={handleSubmit(onSubmit)} className={classes.formFields}>
        <TextField
          name="studentFirstName"
          inputRef={register}
          label="Student first name"
          error={!!errors.studentFirstName}
          helperText={errors.studentFirstName?.message}
          fullWidth
          required
          margin="normal"
        />
        <TextField
          name="studentLastName"
          inputRef={register}
          label="Student last name"
          error={!!errors.studentLastName}
          helperText={errors.studentLastName?.message}
          fullWidth
          required
          margin="normal"
        />
        <Box mt={1} mb={1}>
          <Typography variant="body2">Student</Typography>
          <Controller
            name="attendeeType"
            control={control}
            defaultValue="adult"
            render={props => (
              <RadioGroup
                row
                value={props.value}
                onChange={event => props.onChange(event.target.value)}
              >
                <FormControlLabel
                  value="adult"
                  control={<Radio color="primary" />}
                  label="Adult"
                />
                <FormControlLabel
                  value="junior"
                  control={<Radio color="primary" />}
                  label="Junior"
                />
              </RadioGroup>
            )}
          />
        </Box>
        {isJunior && (
          <>
            <Typography variant="body1" color="textSecondary">
              Parent or guardian
            </Typography>
            <TextField
              name="guardianFirstName"
              inputRef={register}
              label="Parent or guardian First name"
              error={!!errors.guardianFirstName}
              helperText={errors.guardianFirstName?.message}
              fullWidth
              required
              margin="normal"
            />
            <TextField
              name="guardianLastName"
              inputRef={register}
              label="Parent or guardian Last name"
              error={!!errors.guardianLastName}
              helperText={errors.guardianLastName?.message}
              fullWidth
              required
              margin="normal"
            />
          </>
        )}
        <EmailFieldWithSuggestions
          label={isJunior ? 'Parent or guardian Email' : 'Email'}
          control={control}
          name="email"
          errors={errors}
          classes={{}}
          fullWidth
          required
          margin="normal"
        />
        <PhoneField
          name="phone"
          inputRef={register}
          label={isJunior ? 'Parent or guardian Phone number' : 'Phone number'}
          error={!!errors.phone}
          helperText={errors.phone?.message}
          fullWidth
          required
          className={classes.phoneField}
        />
        <Box mt={3} mb={4}>
          <Button type="submit" variant="contained" color="primary">
            Continue
          </Button>
        </Box>
      </form>
    </Grid>
  )
}

GuestStudentForm.propTypes = {
  currentUrl: PropTypes.string.isRequired,
  showDescription: PropTypes.bool,
  stepText: PropTypes.string,
  progressValue: PropTypes.number,
}

export default GuestStudentForm
